import type { TxComponent } from '@landfolk/styling/types/landstyle'
import { Slot } from '@radix-ui/react-slot'
import {
  Children,
  cloneElement,
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  isValidElement,
  type MouseEvent,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'

import Checkmark from '../../icons/Checkmark.svg'
import Cross from '../../icons/Cross.svg'
import { variant } from '../../utils/styled'
import { Spinner } from '../display/Spinner'

export const KINDS = ['emphasised', 'flat', 'outline', 'text'] as const
export const STATES = ['loading', 'success', 'error'] as const
export const TIMEOUT = 1000

export type ButtonKind = (typeof KINDS)[number]
export type ButtonState = (typeof STATES)[number] | false

type ButtonOwnProps = {
  kind?: ButtonKind
  state?: ButtonState
  light?: boolean
  size?: 'sm' | 'lg'
  danger?: boolean
  stateTimeout?: number
  asChild?: boolean
  children?: ReactNode
  onClick?: MouseEventHandler<HTMLElement>
}

export type ButtonProps = ButtonOwnProps &
  Omit<ComponentPropsWithoutRef<'button'>, keyof ButtonOwnProps> &
  TxComponent

export type ButtonLinkSharedProps = Pick<
  ButtonProps,
  'kind' | 'light' | 'size' | 'danger' | 'state' | 'disabled' | 'onClick'
>

type ButtonIconProps = {
  children: ReactNode
  className?: string
}

type ButtonLabelProps = {
  children: ReactNode
  className?: string
}

const ButtonIcon = ({ children, className }: ButtonIconProps) => (
  <span
    aria-hidden="true"
    role="presentation"
    className={className}
    data-button-icon
  >
    {children}
  </span>
)

ButtonIcon.displayName = 'Button.Icon'

const ButtonLabel = ({ children, className }: ButtonLabelProps) => (
  <span className={className} data-button-label>
    {children}
  </span>
)

ButtonLabel.displayName = 'Button.Label'

type ButtonElement = ElementRef<'button'>

type ButtonComponent = ReturnType<
  typeof forwardRef<ButtonElement, ButtonProps>
> & {
  Icon: typeof ButtonIcon
  Label: typeof ButtonLabel
}

const ButtonRoot = forwardRef<ButtonElement, ButtonProps>(
  (
    {
      kind = 'emphasised',
      state,
      light,
      danger,
      size = 'lg',
      stateTimeout = TIMEOUT,
      children,
      disabled,
      className,
      style,
      asChild,
      onClick,
      type,
      ...rest
    },
    forwardedRef,
  ) => {
    const Component = asChild ? Slot : 'button'
    const rootChild = asChild ? getAsChildElement(children) : undefined
    const buttonChildren = rootChild?.props.children ?? children
    const [localState, setLocalState] = useState(state)
    const canonicalKind = kind
    const descriptor = useMemo(
      () => getButtonDescriptor({ children: buttonChildren }),
      [buttonChildren],
    )

    useEffect(() => {
      setLocalState(state)
    }, [state])

    useEffect(() => {
      let timeout: NodeJS.Timeout

      if (localState === 'success' || localState === 'error') {
        timeout = setTimeout(() => setLocalState(undefined), stateTimeout)
      }

      return () => clearTimeout(timeout)
    }, [localState, stateTimeout])

    const isLoading = localState === 'loading'
    const isInteractionDisabled = disabled || Boolean(localState)
    const isVisuallyDisabled = Boolean(disabled)
    const isTextKind = canonicalKind === 'text'
    const resolvedSize = size
    const buttonSpacingClasses = getButtonSpacingClasses({
      size: resolvedSize,
      descriptor,
    })

    if (process.env.NODE_ENV === 'development' && descriptor.isIconOnly) {
      const ariaLabel = rest['aria-label']
      if (!ariaLabel) {
        console.warn(
          'Button: Icon-only buttons should have an aria-label for accessibility',
        )
      }
    }

    const stateIconChild =
      localState === 'loading' ? (
        <Spinner
          className={
            resolvedSize === 'lg'
              ? 'h-[1.25rem] w-[1.25rem] shrink-0'
              : 'h-1 w-1 shrink-0'
          }
        />
      ) : localState === 'success' ? (
        <Checkmark
          className={
            resolvedSize === 'lg'
              ? 'h-[1.25rem] w-[1.25rem] shrink-0'
              : 'h-1 w-1 shrink-0'
          }
        />
      ) : localState === 'error' ? (
        <Cross
          className={
            resolvedSize === 'lg'
              ? 'h-[1.25rem] w-[1.25rem] shrink-0'
              : 'h-1 w-1 shrink-0'
          }
        />
      ) : undefined

    const renderedSlots = descriptor.slots.map((slot) =>
      renderDescriptorSlot({
        slot,
        size: resolvedSize,
      }),
    )

    const visibleContent = (
      <>
        {descriptor.contentLayout === 'icon-only' ? (
          renderedSlots
        ) : descriptor.contentLayout === 'inline-gap' ? (
          <span tx="inline-flex items-center gap-x-2 whitespace-nowrap">
            {renderedSlots}
          </span>
        ) : (
          renderedSlots
        )}
        {descriptor.otherChildren}
      </>
    )

    const content = (
      <>
        {stateIconChild ? (
          <span
            aria-hidden="true"
            role="presentation"
            className={[
              'absolute left-0 top-0 inline-flex h-full w-full items-center justify-center',
              getIconVariantClass({ kind: canonicalKind, light }),
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {stateIconChild}
          </span>
        ) : undefined}
        {stateIconChild ? (
          <span tx="inline-flex items-center whitespace-nowrap opacity-0 transition-opacity duration-300">
            {visibleContent}
          </span>
        ) : (
          visibleContent
        )}
      </>
    )

    return (
      <Component
        ref={forwardedRef}
        className={className}
        style={style}
        aria-busy={isLoading || undefined}
        aria-disabled={isInteractionDisabled || undefined}
        data-kind={canonicalKind}
        data-size={resolvedSize}
        data-icon-only={descriptor.isIconOnly || undefined}
        tx={[
          tx`
            relative inline-flex w-fit items-center justify-center
            whitespace-nowrap no-underline
            rounded-full border
            transition duration-100 ease-in
            focus-visible:(outline-2 outline-offset-2 outline-february)
            disabled:cursor-default
            [-webkit-tap-highlight-color:transparent]
          `,
          isTextKind ? tx`font-normal` : tx`font-bold`,
          variant(
            resolvedSize === 'lg' &&
              tx`min-h-[3rem] py-2.5 leading-tight active:scale-[99%]`,
            resolvedSize === 'sm' &&
              tx`min-h-[2rem] py-1 text-f7 leading-none active:scale-[97%]`,
          ),
          buttonSpacingClasses,
          isTextKind && tx`border-transparent !px-0 !underline`,
          getVariantStyles({
            disabled: isVisuallyDisabled,
            light,
            danger,
            kind: canonicalKind,
          }),
          localState === 'error' && tx`animate-shake`,
        ]}
        data-state-active={Boolean(localState) || undefined}
        disabled={asChild ? undefined : isInteractionDisabled}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          if (isInteractionDisabled && asChild) {
            event.preventDefault()
            event.stopPropagation()
            return
          }

          onClick?.(event)
        }}
        type={asChild ? undefined : type ?? 'button'}
        {...rest}
      >
        {asChild && rootChild
          ? cloneElement(rootChild, { children: content })
          : content}
      </Component>
    )
  },
)

ButtonRoot.displayName = 'Button'

const Button = Object.assign(ButtonRoot, {
  Icon: ButtonIcon,
  Label: ButtonLabel,
}) as ButtonComponent

export { Button }

type IconChildElement = ReactElement<{ className?: string }>
type ButtonIconSlotElement = ReactElement<ButtonIconProps, typeof ButtonIcon>
type ButtonLabelSlotElement = ReactElement<ButtonLabelProps, typeof ButtonLabel>

type ButtonDescriptorSlot =
  | {
      kind: 'icon'
      key: string
      position: 'before' | 'after'
      content: ReactNode
      element?: ButtonIconSlotElement
    }
  | {
      kind: 'label'
      key: string
      content: ReactNode
      wrap: boolean
      element?: ButtonLabelSlotElement
    }

type ButtonDescriptor = {
  slots: ButtonDescriptorSlot[]
  otherChildren?: ReactNode
  hasIconBefore: boolean
  hasIconAfter: boolean
  isIconOnly: boolean
  contentLayout: 'plain' | 'inline-gap' | 'icon-only'
}

function getAsChildElement(children: ReactNode) {
  const child = Children.only(children)

  if (!isValidElement(child)) {
    throw new Error(
      'Button with asChild expects a single valid React element child',
    )
  }

  return child as ReactElement<{ children?: ReactNode }>
}

function renderIconChild(child: ReactNode, size: 'sm' | 'lg') {
  if (!isValidElement<{ className?: string }>(child)) return child

  const iconChild = child as IconChildElement

  return cloneElement(iconChild, {
    className: [iconChild.props.className, getIconSizeClass(size)]
      .filter(Boolean)
      .join(' '),
  })
}

function renderDescriptorSlot({
  slot,
  size,
}: {
  slot: ButtonDescriptorSlot
  size: 'sm' | 'lg'
}) {
  if (slot.kind === 'icon') {
    if (slot.element) {
      return cloneElement(slot.element, {
        key: slot.key,
        className: getIconSlotClass(size),
        children: renderIconChild(slot.content, size),
      })
    }

    return (
      <span
        key={slot.key}
        aria-hidden="true"
        role="presentation"
        className={getIconSlotClass(size)}
      >
        {renderIconChild(slot.content, size)}
      </span>
    )
  }

  if (slot.element) {
    return cloneElement(slot.element, {
      key: slot.key,
      className: 'inline-flex items-center whitespace-nowrap leading-none',
    })
  }

  if (slot.wrap) {
    return (
      <span
        key={slot.key}
        tx="inline-flex items-center whitespace-nowrap leading-none"
      >
        {slot.content}
      </span>
    )
  }

  return slot.content
}

function getButtonSpacingClasses({
  size,
  descriptor,
}: {
  size: 'sm' | 'lg'
  descriptor: ButtonDescriptor
}) {
  if (descriptor.isIconOnly) {
    return variant(
      size === 'lg' && tx`!h-2.5 !w-2.5 !p-0`,
      size === 'sm' && tx`!h-2 !w-2 !p-0`,
    )
  }

  if (size === 'lg') {
    return variant(
      !descriptor.hasIconBefore && !descriptor.hasIconAfter && tx`px-3.5`,
      descriptor.hasIconBefore &&
        !descriptor.hasIconAfter &&
        tx`pl-[1.25rem] pr-3.5`,
      !descriptor.hasIconBefore &&
        descriptor.hasIconAfter &&
        tx`pl-3.5 pr-[1.25rem]`,
      descriptor.hasIconBefore &&
        descriptor.hasIconAfter &&
        tx`pl-[1.25rem] pr-[1.25rem]`,
    )
  }

  return variant(
    !descriptor.hasIconBefore && !descriptor.hasIconAfter && tx`px-3`,
    descriptor.hasIconBefore && !descriptor.hasIconAfter && tx`px-3`,
    !descriptor.hasIconBefore && descriptor.hasIconAfter && tx`pl-3 pr-2.5`,
    descriptor.hasIconBefore && descriptor.hasIconAfter && tx`pl-3 pr-2.5`,
  )
}

function getIconSizeClass(size: 'sm' | 'lg') {
  return size === 'lg' ? 'h-[1.25rem] w-[1.25rem] shrink-0' : 'h-1 w-1 shrink-0'
}

function getIconSlotClass(size: 'sm' | 'lg') {
  return [
    'inline-flex shrink-0 items-center justify-center',
    getIconSizeClass(size).replace(' shrink-0', ''),
  ].join(' ')
}

function getIconVariantClass({
  kind,
  light,
}: {
  kind: ButtonKind
  light?: boolean
}) {
  if (kind === 'outline' && light) return 'text-white'
  if (kind === 'outline') return 'text-february'
  if (kind === 'flat' && light) return 'text-february'
  if (kind === 'text' && light) return 'text-white'
  if (kind === 'text') return 'text-february'

  return 'text-white'
}

function isButtonIconElement(child: ReactNode): child is ButtonIconSlotElement {
  return isValidElement(child) && child.type === ButtonIcon
}

function isButtonLabelElement(
  child: ReactNode,
): child is ButtonLabelSlotElement {
  return isValidElement(child) && child.type === ButtonLabel
}

function getButtonDescriptor({
  children,
}: {
  children: ReactNode
}): ButtonDescriptor {
  const childArray = Children.toArray(children)
  const hasExplicitStructure = childArray.some(
    (child) => isButtonIconElement(child) || isButtonLabelElement(child),
  )

  if (!hasExplicitStructure) {
    return {
      slots: [
        {
          kind: 'label' as const,
          key: 'label',
          content: children,
          wrap: false,
        },
      ],
      otherChildren: undefined,
      hasIconBefore: false,
      hasIconAfter: false,
      isIconOnly: false,
      contentLayout: 'plain',
    }
  }

  const leadingIcons: ButtonIconSlotElement[] = []
  const trailingIcons: ButtonIconSlotElement[] = []
  const labels: ButtonLabelSlotElement[] = []
  const otherChildren: ReactNode[] = []
  let seenLabel = false

  for (const child of childArray) {
    if (isButtonLabelElement(child)) {
      seenLabel = true
      labels.push(child)
      continue
    }

    if (isButtonIconElement(child)) {
      if (seenLabel) {
        trailingIcons.push(child)
      } else {
        leadingIcons.push(child)
      }
      continue
    }

    otherChildren.push(child)
  }

  const hasIconBefore = leadingIcons.length > 0
  const hasIconAfter = trailingIcons.length > 0
  const hasLabel = labels.length > 0
  const isIconOnly = !hasLabel && (hasIconBefore || hasIconAfter)

  return {
    slots: [
      ...leadingIcons.map((iconElement, index) => ({
        kind: 'icon' as const,
        key: `icon-before-${index}`,
        position: 'before' as const,
        content: iconElement.props.children,
        element: iconElement,
      })),
      ...labels.map((labelElement, index) => ({
        kind: 'label' as const,
        key: `label-${index}`,
        content: labelElement.props.children,
        wrap: true,
        element: labelElement,
      })),
      ...trailingIcons.map((iconElement, index) => ({
        kind: 'icon' as const,
        key: `icon-after-${index}`,
        position: 'after' as const,
        content: iconElement.props.children,
        element: iconElement,
      })),
    ],
    otherChildren: otherChildren.length > 0 ? otherChildren : undefined,
    hasIconBefore,
    hasIconAfter,
    isIconOnly,
    contentLayout: isIconOnly
      ? 'icon-only'
      : hasIconBefore || hasIconAfter
        ? 'inline-gap'
        : 'plain',
  }
}

function getVariantStyles({
  disabled,
  light,
  danger,
  kind,
}: Pick<ButtonProps, 'light' | 'danger'> & {
  disabled?: boolean
  kind: ButtonKind
}) {
  if (disabled) {
    return variant(
      tx`border-neutral-200 bg-neutral-100 text-neutral-600/50`,
      light && tx`border-neutral-600 bg-neutral-700 text-neutral-300`,
      kind === 'text' && tx`border-transparent bg-transparent text-neutral-300`,
      kind === 'text' &&
        light &&
        tx`border-transparent bg-transparent text-neutral-600`,
    )
  }

  if (danger) {
    return variant(
      tx`
        border-error-700 text-white shadow-widget
        bg-gradient-to-b from-error-600 to-error-700
        hover:text-white active:shadow-none can-hover:hover:brightness-110
      `,
      light &&
        kind === 'emphasised' &&
        tx`
          border-error-600 text-white shadow-widget
          bg-gradient-to-b from-error-600 to-error-700
          hover:text-white active:shadow-none can-hover:hover:brightness-110
        `,
      kind === 'outline' &&
        tx`border-error bg-transparent text-error hover:text-error can-hover:hover:(border-forest-700 text-forest-700)`,
      kind === 'outline' &&
        light &&
        tx`border-error bg-transparent text-error-100 hover:text-error-100 can-hover:hover:text-beach-300`,
      kind === 'flat' &&
        tx`border-transparent bg-error-700 text-white hover:text-white can-hover:hover:bg-error-600`,
      kind === 'flat' &&
        light &&
        tx`border-transparent bg-error-100 text-error hover:text-error can-hover:hover:bg-white`,
      kind === 'text' &&
        tx`border-transparent bg-transparent text-error underline hover:text-error can-hover:hover:text-forest-700`,
      kind === 'text' &&
        light &&
        tx`border-transparent bg-transparent text-error-100 underline hover:text-error-100 can-hover:hover:text-beach-300`,
    )
  }

  if (kind === 'emphasised') {
    return variant(
      tx`
        border-forest-800 text-white shadow-widget
        bg-gradient-to-b from-forest-700 to-forest-800
        hover:text-white active:shadow-none can-hover:hover:brightness-110
      `,
      light &&
        tx`
          border-forest-700 text-white shadow-widget
          bg-gradient-to-b from-forest-700 to-forest-800
          hover:text-white active:shadow-none can-hover:hover:brightness-110
        `,
    )
  }

  if (kind === 'flat') {
    return variant(
      tx`
        border-transparent bg-february text-white
        hover:text-white can-hover:hover:bg-forest-800
        active:bg-forest-800
      `,
      light &&
        tx`
          border-transparent bg-neutral-100 text-february
          hover:text-february can-hover:hover:bg-white
          active:bg-white
        `,
    )
  }

  if (kind === 'outline') {
    return variant(
      tx`
        border-february-muted bg-transparent text-february
        hover:text-february can-hover:hover:(border-forest-700 text-forest-700)
        active:(border-forest-800 bg-neutral-200)
      `,
      light &&
        tx`
          border-neutral-300 bg-transparent text-white
          hover:text-white can-hover:hover:(border-beach-300 text-beach-300)
          active:(border-beach-300 bg-white/5)
        `,
    )
  }

  return variant(
    tx`
      border-transparent bg-transparent text-february underline
      hover:text-february can-hover:hover:text-forest-700
      active:text-february
    `,
    light &&
      tx`
        border-transparent bg-transparent text-white underline
        hover:text-white can-hover:hover:text-beach-300
        active:text-white
      `,
  )
}
